import type { ConsentClassRow } from '../../../shared/api/rights'
import type { ConsentStatus } from '../../../shared/api/parent'

/**
 * S3's fold — *"which children have consent, which are pending, which
 * revoked"*.
 *
 * ⚠️ **THE WHOLE SCREEN IS INVENTED.** settings-and-controls.md §4 gives S3 one
 * line and `wireframes.md`'s C-section stops at C5, so there is no layout to
 * follow anywhere in the repo. The precedent for doing it anyway is OD-55,
 * where §8.3's write routes were invented against a doc that documented only
 * `GET /classes` — recorded as a decision rather than presented as a spec. 📋
 * Reported.
 *
 * 🔴 THE ONE DESIGN DECISION THAT IS NOT COSMETIC: **PENDING IS THE ORDINARY
 * STATE, NOT A FAILURE.**
 *
 * OD-50 kept `ParentalConsent` unwritten until §9.4, so on the morning this
 * ships every child in every school is pending. A dashboard that sorts the
 * worst-looking state to the top and paints it red would read as a data-loss
 * incident, and the school's first action would be to phone us rather than to
 * phone the parents. The screen's actual job is *"the screen that makes chasing
 * parents possible"* — so pending is a **queue**, with a count and a next step.
 *
 * Pure, because this repo has no DOM test renderer (OD-60) and the ordering and
 * the counts are the only decisions on the screen.
 */

export type ConsentSort = 'roll' | 'status'

export interface ConsentSummary {
  granted: number
  pending: number
  revoked: number
  expired: number
  total: number
}

/**
 * 🔴 THE ORDER `status` SORTS INTO, AND IT IS NOT "WORST FIRST".
 *
 * `PENDING` leads because it is the only state with an action attached: invite
 * the parent to complete the OTP flow. `REVOKED` is a decision a parent made
 * and is explicitly **not** to be chased — putting it above pending would turn
 * the screen into a list of families to pester. `EXPIRED` sits between them: it
 * needs a renewal, which is a real action, but a less urgent one than a child
 * whose data was never permitted at all.
 */
const STATUS_ORDER: Record<ConsentStatus, number> = {
  PENDING: 0,
  EXPIRED: 1,
  REVOKED: 2,
  GRANTED: 3,
}

export function summariseConsent(rows: readonly ConsentClassRow[]): ConsentSummary {
  const summary: ConsentSummary = {
    granted: 0,
    pending: 0,
    revoked: 0,
    expired: 0,
    total: rows.length,
  }
  for (const row of rows) {
    if (row.status === 'GRANTED') summary.granted += 1
    else if (row.status === 'REVOKED') summary.revoked += 1
    else if (row.status === 'EXPIRED') summary.expired += 1
    else summary.pending += 1
  }
  return summary
}

/**
 * 🔴 SORTS, NEVER FILTERS — the same rule T6 established for the roster.
 *
 * *"A filtered roster hides who the teacher has already done, and a child who
 * drops out mid-lesson vanishes."* Here it is sharper: a school admin working
 * down a filtered consent list cannot see which parents they have already
 * called, and a child whose consent arrives mid-session disappears from the
 * screen the school is accountable for.
 */
export function orderConsentRows(
  rows: readonly ConsentClassRow[],
  sort: ConsentSort,
): ConsentClassRow[] {
  const out = [...rows]

  out.sort((a, b) => {
    if (sort === 'status') {
      const byStatus = STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
      if (byStatus !== 0) return byStatus
    }
    return compareRoll(a, b)
  })

  return out
}

/**
 * Roll numbers are strings in the schema but are read as numbers by everyone
 * who uses them, so `10` must come after `2`.
 *
 * ⚠️ The identical rule lives in the teacher tier's `rosterOrder.ts`. Not
 * imported: `features/dashboards/school` importing from
 * `features/dashboards/teacher` would be a sideways dependency, which
 * `folderstructure.md` forbids and eslint enforces for `shared/`. 📋 Reported
 * as a candidate for `shared/lib` once a third caller exists — two is a
 * coincidence.
 */
function compareRoll(a: ConsentClassRow, b: ConsentClassRow): number {
  const ra = a.rollNumber
  const rb = b.rollNumber
  // A child with no roll number goes last, not first: an unnumbered row at the
  // top of a register reads as row zero.
  if (ra === null && rb === null) return a.name.localeCompare(b.name)
  if (ra === null) return 1
  if (rb === null) return -1

  const na = Number(ra)
  const nb = Number(rb)
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb
  return ra.localeCompare(rb)
}

/**
 * The headline sentence, chosen from the summary.
 *
 * 🔴 IT IS WHY THE SCREEN EXISTS. A school reading "18 pending" with no
 * explanation concludes the product lost their consent records. The sentence
 * has to say the true thing: **no consent has been captured yet, and the OTP
 * flow is the mechanism.**
 */
export type ConsentHeadline = 'nobodyEnrolled' | 'noneCaptured' | 'someOutstanding' | 'allGranted'

export function consentHeadline(summary: ConsentSummary): ConsentHeadline {
  if (summary.total === 0) return 'nobodyEnrolled'
  // Every single child pending: the day-one state, and the one most likely to
  // be misread as a fault.
  if (summary.pending === summary.total) return 'noneCaptured'
  if (summary.pending > 0 || summary.expired > 0) return 'someOutstanding'
  return 'allGranted'
}
